import React, { useState, useEffect, Fragment } from "react";
import { NotificationManager } from "../../../../../../components/common/react-notifications";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  CustomInput,
  InputGroup,
  InputGroupText,
  InputGroupAddon,
} from "reactstrap";
import { AxiosAPI } from "../../../../../../helpers/AxiosAPI";
import { gAPI_Twilio_Templates } from "../../../../../../constants/APIs";

const CHANNEL_OPTIONS = ["WhatsApp", "SMS", "PhoneSuite"];

const FIELDS = [
  { key: "guest_first_name", label: "First Name" },
  { key: "guest_last_name", label: "Last Name" },
  { key: "check_in_date", label: "Check-in Date" },
  { key: "check_out_date", label: "Check-out Date" },
  { key: "guest_email", label: "Email Address" },
  { key: "guest_phone", label: "Phone Number" },
  { key: "room_number", label: "Room Number" },
  { key: "room_type", label: "Room Type Name" },
];

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "ar", label: "Arabic" },
  { code: "fr", label: "French" },
  { code: "es", label: "Spanish" },
  { code: "pt_BR", label: "Portuguese (Brazil)" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "id", label: "Indonesian" },
  { code: "vi", label: "Vietnamese" },
];

const TEMPLATE_TYPES = [
  { value: "text", label: "Text" },
  { value: "media", label: "Media (Coming Soon)" },
  { value: "cta", label: "Call to Action (Coming Soon)" },
  { value: "card", label: "Card (Coming Soon)" },
  { value: "quick_reply", label: "Quick Reply (Coming Soon)" },
];



const AddNewTemplate = ({
  propertyId,
  groupId,
  userName,
  visible,
  toggleModal,
  formLabel,
  templateData,
  refreshTemplates,
  defaultChannel
}) => {
  const [name, setName] = useState("");
  const [channels, setChannels] = useState([]);
  const [message, setMessage] = useState("");
  const [placeholderMap, setPlaceholderMap] = useState({});
  const [placeholderErrors, setPlaceholderErrors] = useState({});
  const [templateId, setTemplateId] = useState(null);
  // const [messagingServiceSid, setMessagingServiceSid] = useState(null);
  const [templateType, setTemplateType] = useState(null);
  const [messageType, setMessageType] = useState("None");
  const [templateContentType, setTemplateContentType] = useState("None");
  const [language, setLanguage] = useState(null);
  const isVoice = defaultChannel === "PhoneSuite";
  /* ================= PREFILL ON EDIT ================= */
  useEffect(() => {
    if (templateData) {
      setName(templateData.name || "");
      setChannels(templateData.channel || []);
      setMessage(templateData.templateBody || "");
      setTemplateId(templateData.templateId || "");
      // setMessagingServiceSid(templateData.messagingServiceSid || "");
      setTemplateType(templateData.templateType || "");
      setMessageType(templateData.messageType || "None");
      setTemplateContentType(templateData.templateContentType || "None");
      setLanguage((templateData.language || "").toLowerCase());
      const map = {};
      (templateData.placeholders || []).forEach(p => {
        map[p.name] = p.value;
      });
      setPlaceholderMap(map);
      setPlaceholderErrors({});
    } else {
      setName("");
      if (defaultChannel === "sms") {
        setChannels(["SMS"]);
        setMessageType("SMS");
        setTemplateContentType("None");
      } else if (defaultChannel === "whatsapp") {
        setChannels(["WhatsApp"]);
        setMessageType("None");
        setTemplateContentType("Text");
      } else if (defaultChannel === "PhoneSuite") {
        setChannels(["PhoneSuite"]);
        setMessageType("None");
        setTemplateContentType("None");
      } else {
        setChannels([]);
        setMessageType("None");
        setTemplateContentType("None");
      }

      setMessage("");
      setTemplateId("");
      // setMessagingServiceSid("");
      setTemplateType("");
      setLanguage("");
      setPlaceholderMap({});
      setPlaceholderErrors({});
    }
  }, [templateData, visible]);

  const handleChannelChange = (channel) => {
    setChannels(prev => {
      const updatedChannels = prev.includes(channel)
        ? prev.filter(c => c !== channel)
        : [...prev, channel];

      if (updatedChannels.includes("SMS")) {
        if (messageType === "None") {
          setMessageType("SMS");
        }
      } else {
        setMessageType("None");
      }

      if (updatedChannels.includes("WhatsApp")) {
        if (templateContentType === "None") {
          setTemplateContentType("Text");
        }
      } else {
        setTemplateContentType("None");
      }

      return updatedChannels;
    });
  };
  const validatePlaceholders = () => {
    const placeholdersInMessage = extractPlaceholders(message);
    const errors = {};

    placeholdersInMessage.forEach(ph => {
      if (!placeholderMap[ph]) {
        errors[ph] = "This field is required";
      }
    });

    setPlaceholderErrors(errors);

    return Object.keys(errors).length === 0;
  };
  const extractPlaceholders = (text) =>
    [...new Set(
      (text.match(/{{\s*([\w-]+)\s*}}/g) || [])
        .map(p => p.replace(/[{}]/g, ""))
    )];


  /* ================= CREATE ================= */
  const handleCreateTemplate = async () => {
    if (!validatePlaceholders()) return;
    const placeholders = Object.entries(placeholderMap).map(
      ([name, value]) => ({ name, value })
    );

    const body = {
      groupId,
      propertyId,
      name,
      templateId: isVoice ? '' : templateId,
      templateBody: message,
      channel: channels,
      templateType: isVoice ? "text" : templateType,
      language: (language || "").toLowerCase(),
      createdBy: userName,
      placeholders,
      messageType,
      templateContentType,
    };

    try {
      const res = await AxiosAPI({
        method: "post",
        url: gAPI_Twilio_Templates,
        data: body,
      });
      if (res.data?.isTwilioTemplateCreated === false) {
        console.log("Notification condition hit");
        NotificationManager.warning(
          "SMS template could not be created.",
          "",
          5000
        );
      }
      toggleModal();
      refreshTemplates?.();
    } catch (err) {
      console.error("Failed to create template", err);
    }
  };

  /* ================= UPDATE ================= */
  const handleUpdateTemplate = async () => {
    if (!validatePlaceholders()) return;
    const placeholders = Object.entries(placeholderMap).map(
      ([name, value]) => ({ name, value })
    );

    const body = {
      name,
      templateBody: message,
      templateType: isVoice ? "text" : templateType,
      templateId: isVoice ? '' : templateId,
      language: (language || "").toLowerCase(),
      channel: channels,
      updatedBy: userName,
      placeholders,
      messageType,
      templateContentType,
    };

    try {
      const res_Update = await AxiosAPI({
        method: "put",
        url: `${gAPI_Twilio_Templates}/${templateData.id}`,
        params: {
          group_id: groupId,
        },
        data: body,
      });
      if (res_Update.data?.isTwilioTemplateCreated === false) {
        NotificationManager.warning(
          "SMS template could not be updated.",
          "",
          5000
        );
      }
      toggleModal();
      refreshTemplates?.();
    } catch (error) {
      console.error(
        "Failed to update template",
        error?.response?.data || error
      );
    }
  };

  const handleSave = () => {
    templateData ? handleUpdateTemplate() : handleCreateTemplate();
  };

  const isWhatsApp = channels.includes("WhatsApp");
  const isSMSOnly = channels.includes("SMS") && !isWhatsApp;
  const isAIVoice = channels.includes("PhoneSuite") && !isWhatsApp && !isSMSOnly;
  const noChannelSelected = channels.length === 0;
  const isSMSSelected = channels.includes("SMS");
  return (
    <Fragment>
      <style>
        {`
      select option:disabled {
        background-color: #d0d0d0;
        color: #555;
        cursor: not-allowed;
      }
    `}
      </style>
      <Modal isOpen={visible} toggle={toggleModal} wrapClassName="modal-right">
        <ModalHeader toggle={toggleModal}>{formLabel}</ModalHeader>

        <ModalBody>
          <Label>Name</Label>
          <Input className='input_sty' value={name} onChange={(e) => setName(e.target.value)} placeholder={
            isSMSSelected
              ? messageType === "SMS"
                ? "Enter SMS Template Name"
                : "Enter RCS Template Name"
              : "Enter Twilio Template Name"
          } />

          {!isVoice && (
            <>
              <Label className="mt-3">Template Id</Label>
              <Input className='input_sty'
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                placeholder={
                  isSMSSelected && messageType === "SMS"
                    ? "Will be generated automatically"
                    : isSMSSelected && messageType === "RCS"
                      ? "Enter Template ID (HX...)"
                      : "Enter Twilio Template ID"
                }
                disabled={isSMSSelected && messageType === "SMS"}
                style={{
                  backgroundColor:
                    isSMSSelected && messageType === "SMS" ? "#f5f5f5" : "",
                  cursor:
                    isSMSSelected && messageType === "SMS" ? "not-allowed" : "text",
                  opacity:
                    isSMSSelected && messageType === "SMS" ? 0.7 : 1,
                }}
              />
              {/* {isSMSSelected && messageType === "RCS" && (
                <>
                  <Label className="mt-3">Messaging Service SID</Label>
                  <Input
                    className="input_sty"
                    value={messagingServiceSid}
                    onChange={(e) => setMessagingServiceSid(e.target.value)}
                    placeholder="Enter Messaging Service SID (MG...)"
                  />
                </>
              )} */}
              {/* <Label className="mt-3">Template Type</Label>
              <Input className='input_sty'
                type="select"
                value={templateType || ""}
                onChange={(e) => setTemplateType(e.target.value)}
              >
                <option value="">Select Template Type</option>

                {TEMPLATE_TYPES
                  .filter(t =>
                    (noChannelSelected || isSMSOnly)
                      ? t.value === "text"
                      : true
                  )
                  .map(t => {
                    const disabled = isWhatsApp && t.value !== "text";

                    return (
                      <option
                        key={t.value}
                        value={t.value}
                        disabled={disabled}
                      >
                        {t.label}
                      </option>
                    );
                  })}
              </Input> */}
            </>
          )}
          <Label className="mt-3">Language</Label>
          <Input className='input_sty'
            type="select"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option value="">Select Language</option>
            {LANGUAGES.map(lang => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </Input>

          <Label className="mt-3">Channels</Label>
          {CHANNEL_OPTIONS.map((ch) => (
            <CustomInput
              key={ch}
              type="checkbox"
              label={ch == 'PhoneSuite' ? 'Voice Call' : ch}
              checked={channels.includes(ch)}
              onChange={() => handleChannelChange(ch)}
            />
          ))}
          {/* {!templateData && isSMSSelected && (
            <div className="mt-3">
              <Label>
                Twilio
              </Label>

              <CustomInput
                id="create-in-twilio"
                type="checkbox"
                label="Create SMS Template in Twilio"
                checked={createInTwilio}
                onChange={(e) => setCreateInTwilio(e.target.checked)}
              />
            </div>
          )} */}
          {isSMSSelected && (
            <>
              <Label className="mt-3">Message Type</Label>

              <CustomInput
                type="radio"
                id="message-type-sms"
                name="messageType"
                label="SMS"
                value="SMS"
                checked={messageType === "SMS"}
                onChange={(e) => setMessageType(e.target.value)}
              />

              <CustomInput
                type="radio"
                id="message-type-rcs"
                name="messageType"
                label="RCS"
                value="RCS"
                checked={messageType === "RCS"}
                onChange={(e) => setMessageType(e.target.value)}
              />
            </>
          )}
          {isWhatsApp && (
            <>
              <Label className="mt-3">Template Content Type</Label>

              <CustomInput
                type="radio"
                id="template-type-text"
                name="templateContentType"
                label="Text"
                value="Text"
                checked={templateContentType === "Text"}
                onChange={(e) => setTemplateContentType(e.target.value)}
              />

              <CustomInput
                type="radio"
                id="template-type-card"
                name="templateContentType"
                label="Card"
                value="Card"
                checked={templateContentType === "Card"}
                onChange={(e) => setTemplateContentType(e.target.value)}
              />
            </>
          )}
          <Label className="mt-3">Message</Label>
          <Input className='input_sty mb-2'
            type="textarea"
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={
              isSMSSelected
                ? messageType === "SMS"
                  ? "Enter SMS Template Message"
                  : "Enter RCS Template Message"
                : "Enter Twilio Template Message"
            }
          />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
            }}
          >
            {extractPlaceholders(message).map((ph) => {
              const hasError = placeholderErrors[ph];

              return (
                <div key={ph} style={{ marginBottom: 5 }}>
                  <InputGroup
                    style={{
                      border: hasError ? "1px solid red" : "1px solid #ddd",
                      borderRadius: 6,
                    }}
                  >
                    {/* LEFT: Placeholder */}
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText
                        style={{
                          background: "#f8f9fa",
                          fontWeight: 600,
                          minWidth: 120,
                          justifyContent: "center",
                        }}
                      >
                        {`{{${ph}}}`}
                      </InputGroupText>
                    </InputGroupAddon>

                    {/* RIGHT: Dropdown */}
                    <Input className='input_sty'
                      type="select"
                      value={placeholderMap[ph] || ""}
                      onChange={(e) => {
                        setPlaceholderMap({
                          ...placeholderMap,
                          [ph]: e.target.value,
                        });

                        setPlaceholderErrors((prev) => {
                          const copy = { ...prev };
                          delete copy[ph];
                          return copy;
                        });
                      }}
                    >
                      <option value="">Select Field</option>
                      {FIELDS.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                        </option>
                      ))}
                    </Input>
                  </InputGroup>

                  {/* ERROR MESSAGE */}
                  {hasError && (
                    <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>
                      Selection required
                    </div>
                  )}
                </div>
              );
            })}

          </div>


        </ModalBody>

        <ModalFooter>
          <Button color="primary" onClick={handleSave}>
            {templateData ? "Update" : "Save"}
          </Button>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default AddNewTemplate;